import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Grape } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";

export default function LoginPage() {
  const { t } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const result = await login(email, password);
    setLoading(false);
    if (result.success) {
      toast.success(t("common.success"));
      const from = location.state?.from?.pathname;
      const roleDashboard = result.user.role === "admin" ? "/admin" : `/dashboard/${result.user.role}`;
      navigate(from || roleDashboard);
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="max-w-md mx-auto px-6 py-16 sm:py-24">
      <div className="text-center mb-8">
        <Grape className="h-8 w-8 text-[#4ba3b7] mx-auto mb-3" />
        <h1 className="font-heading text-2xl sm:text-3xl font-bold">{t("auth.loginTitle")}</h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <Label htmlFor="email">{t("auth.email")}</Label>
          <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                 className="h-12 rounded-xl mt-1.5" data-testid="login-email-input" />
        </div>
        <div>
          <Label htmlFor="password">{t("auth.password")}</Label>
          <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                 className="h-12 rounded-xl mt-1.5" data-testid="login-password-input" />
        </div>
        {error && <p className="text-sm text-[#FF3B30]" data-testid="login-error-message">{error}</p>}
        <Button type="submit" disabled={loading} className="w-full h-12 rounded-xl bg-[#4ba3b7] hover:bg-[#3D8A9A] font-bold" data-testid="login-submit-button">
          {loading ? t("common.loading") : t("auth.loginButton")}
        </Button>
      </form>

      <div className="flex flex-col items-center gap-3 mt-6">
        <Link to="/forgot-password" className="text-sm text-[#4ba3b7] hover:underline" data-testid="login-forgot-password-link">
          {t("auth.forgotPassword")}
        </Link>
        <p className="text-sm text-[#2B2B2B]/60">
          {t("auth.noAccount")}{" "}
          <Link to="/register" className="text-[#4ba3b7] font-semibold hover:underline" data-testid="login-register-link">
            {t("auth.registerButton")}
          </Link>
        </p>
      </div>
    </div>
  );
}
